import axios from 'axios';

const keyCode = Object.freeze({
    'TAB': 9,
    'RETURN': 13,
    'ESC': 27,
    'SPACE': 32,
    'PAGEUP': 33,
    'PAGEDOWN': 34,
    'END': 35,
    'HOME': 36,
    'LEFT': 37,
    'UP': 38,
    'RIGHT': 39,
    'DOWN': 40
});

const bodyElement = document.querySelector('body');
const mediaBreakPoint = '(max-width: 1024px)';
const mq = window.matchMedia(mediaBreakPoint);

class AutoComplete {
    constructor(domNode) {
        this.domNode = domNode;
        this.navContainerEl = this.domNode.closest('.navigation-container');
        this.inputNode = this.domNode.querySelector('#search-input');
        this.submitNode = this.domNode.querySelector('.js-form-search');
        this.clearInputNode = this.domNode.querySelector('#headerSearchClear');
        this.resultContainerNode = this.domNode.querySelector('#headerSearchResultsContainer');
        this.resultNode = this.domNode.querySelector('#headerSearchResults');
        this.backNode = this.domNode.querySelector('.back-button');
        this.closeNode = this.domNode.querySelector('.js-search-close');
        this.liveNode = this.domNode.querySelector('[aria-live]');
        this.inputValue = this.inputNode.value;
        this.results = [];
        this.selectedIndex = -1;

        this.isMenuMobileStyle = false;
        this.isMouseDownOnBackground = false;
        this.instructionText = 'Keyboard users, use up and down arrows to review and enter to select. Touch device users, explore by touch or with swipe gestures.';

        this.inputChangeHandler = this.handleChange.bind(this);
        this.inputFocusHandler = this.handleFocus.bind(this);
        this.inputBlurHandler = this.handleBlur.bind(this);
        this.inputKeyupHandler = this.handleKeyup.bind(this);
        this.inputKeydownHandler = this.handleKeydown.bind(this);
        this.submitClickHandler = this.handleSubmitClick.bind(this);
        this.submitKeydownHandler = this.handleSubmitKeydown.bind(this);
        this.closeClickHandler = this.handleCloseClick.bind(this);
        this.clearClickHandler = this.handleClearClick.bind(this);
    }

    init() {
        if (mq.matches) {
            this.isMenuMobileStyle = true;
            this.addListeners(true);
        } else if (!mq.matches) {
            this.isMenuMobileStyle = false;
            this.addListeners(false);
        }
        window.addEventListener('resize', this.handleWindowResize.bind(this));

        this.inputNode.addEventListener('change', this.inputChangeHandler);
        this.inputNode.addEventListener('keyup', this.inputKeyupHandler);
        this.inputNode.addEventListener('keydown', this.inputKeydownHandler);
        this.inputNode.addEventListener('focus', this.inputFocusHandler);
        this.inputNode.addEventListener('blur', this.inputBlurHandler);
        this.submitNode.addEventListener('click', this.submitClickHandler);
        this.closeNode.addEventListener('click', this.closeClickHandler);
        this.clearInputNode.addEventListener('click', this.clearClickHandler);
    }

    addListeners(mobile) {
        if (mobile) {
            this.domNode.style.position = 'relative';
            this.backNode.addEventListener('click', this.closeClickHandler);
            this.submitNode.removeEventListener('keydown', this.submitKeydownHandler);
            bodyElement.removeEventListener('mousedown', this.handleBackgroundMouseDown.bind(this), true);
            bodyElement.removeEventListener('mouseup', this.handleBackgroundMouseUp.bind(this), true);
        } else {
            this.domNode.style.position = 'absolute';
            this.submitNode.addEventListener('keydown', this.submitKeydownHandler);
            bodyElement.addEventListener('mousedown', this.handleBackgroundMouseDown.bind(this), true);
            bodyElement.addEventListener('mouseup', this.handleBackgroundMouseUp.bind(this), true);
        }
    }

    handleWindowResize() {
        if (mq.matches && !this.isMenuMobileStyle) {
            this.isMenuMobileStyle = true;
            this.addListeners(true);
        } else if (!mq.matches && this.isMenuMobileStyle) {
            this.isMenuMobileStyle = false;
            this.addListeners(false);
        }
    }

    debounce(func, wait) {
        let timeout;
        return function() {
            if (!timeout) func.apply(this, arguments);
            clearTimeout(timeout);
            timeout = setTimeout(()=>{timeout = null}, wait);
        }
    }

    addResultsListers() {
        this.results = this.resultNode.querySelectorAll('li');
        for (let i = 0; i < this.results.length; ++i) {
            const resultItem = this.results[i];
            resultItem.addEventListener('click', this.handleResultsClick.bind(this));
            resultItem.addEventListener('hover', this.handleResultsHover.bind(this));
            resultItem.addEventListener('keydown', this.handleResultsKeydown.bind(this));
            resultItem.index = i;
        }
    }

    fetchResults(value) {
        return new Promise(resolve => {
            if (value.length < 3) {
                return resolve([])
            }

            const url  ='/assets/components/nyas/search.php';
            const params = { q: encodeURI(value) };
            axios.get(url, { params: params })
                .then(response => response.data)
                .then(data => {
                    resolve(data.results);
                })
                .catch(error => {
                    console.error(error);
                });
        })
    }

    buildListHTML(ajaxResults) {
        this.resultNode.innerHTML = '';

        for (let result of ajaxResults) {
            const liElem = document.createElement('li');
            liElem.setAttribute('id', '');
            liElem.classList.add('result-item');
            liElem.setAttribute('role', 'listitem');
            liElem.setAttribute('aria-selected', 'false');
            liElem.tabIndex = -1;

            const resultLinkEl = document.createElement('a');
            resultLinkEl.classList.add('result-item-link');
            resultLinkEl.title = result.pagetitle;
            resultLinkEl.href = result.link;

            const descTextEl = document.createElement('p');
            descTextEl.innerHTML = result.pagetitle;
            descTextEl.classList.add('alternative', 'p-18', 'text-navy');

            resultLinkEl.appendChild(descTextEl);
            liElem.appendChild(resultLinkEl);

            this.resultNode.appendChild(liElem);
        }

        this.showResults();
    }

    announceResults() {
        let number = this.results.length,
            textToRead = number + ' results are available. ' + this.instructionText;

        if (number === 0) {
            textToRead = 'No search results';
        }

        this.liveNode.innerHTML = textToRead;
        setTimeout(function () {
            this.liveNode.innerHTML = '';
        }.bind(this), 1000);
    }

    autocomplete() {
        if (this.inputNode.value === this.inputValue) {
            return;
        }

        // const maxAmount = this.calcResultShowAmount();
        this.inputValue = this.inputNode.value;
        this.fetchResults(this.inputValue).then((data) => {
            if (data.length === 0) {
                this.closeResults();
            } else {
                // if (data.length > maxAmount) {
                //     this.buildListHTML(data.slice(0, data.length - 1));
                //     this.resultNode.appendChild(this.buildLoadMoreHTML());
                // } else {
                // }
                this.buildListHTML(data);
                this.addResultsListers();
            }

            this.announceResults();
        });
    }

    clearSelected() {
        this.selectedIndex = -1;
        this.inputNode.removeAttribute('aria-activedescendant');
        if (this.resultNode.querySelector('[aria-selected="true"]')) {
            this.resultNode.querySelector('[aria-selected="true"]').setAttribute('id', '');
            this.resultNode.querySelector('[aria-selected="true"]').setAttribute('aria-selected', 'false');
        }
    }

    showResults() {
        this.resultContainerNode.setAttribute('aria-hidden', false);
        this.resultContainerNode.classList.add('show');
        this.inputNode.setAttribute('aria-expanded', 'true');
    }

    closeResults() {
        this.clearSelected();
        this.resultContainerNode.setAttribute('aria-hidden', true);
        this.resultContainerNode.classList.remove('show');
        this.inputNode.setAttribute('aria-expanded', 'false');
    }

    markSelected(selectedElem) {
        if (!selectedElem || selectedElem.length === 0) {
            return;
        }
        this.selectedIndex = selectedElem.index;

        const activeItemId = 'selectedOption';
        selectedElem.setAttribute('aria-selected', 'true');
        selectedElem.setAttribute('id', activeItemId);
        selectedElem.focus();
        this.inputNode.setAttribute('aria-activedescendant', activeItemId);
    }

    arrowing(pressedCode) {
        const activeResItem = this.resultNode.querySelector('[aria-selected="true"]');
        let nextResItem;

        if (!this.results.length) {
            return;
        }

        if (pressedCode === keyCode.DOWN) {
            nextResItem = (activeResItem && activeResItem.index < this.results.length - 1 && activeResItem.length !== 0) ? this.results[activeResItem.index + 1] : this.results[0];
        }

        if (pressedCode === keyCode.UP) {
            nextResItem = (activeResItem && activeResItem.length !== 0) ? this.results[activeResItem.index - 1] : this.results[this.results.length - 1];
        }

        this.clearSelected();
        this.markSelected(nextResItem);
    }

    populating() {
        let selectedText = this.resultNode.querySelector('[aria-selected="true"] span') ? this.resultNode.querySelector('[aria-selected="true"] span').getAttribute('data-item-value') : this.inputValue;

        this.inputNode.value = selectedText;
    }

    handleChange(event) {
        this.debounce(this.autocomplete(), 200);
    }

    handleKeyup(event) {
        const pressedCode = event.keyCode;

        if (pressedCode === keyCode.TAB || pressedCode === keyCode.RETURN || pressedCode === keyCode.UP || pressedCode === keyCode.DOWN || pressedCode === keyCode.ESC) {
            return;
        }
        this.debounce(this.autocomplete(), 200);
        // this.autocomplete();
    }

    handleKeydown(event) {
        var flag = false;

        switch (event.keyCode) {
            case keyCode.TAB:
                if (this.resultNode) {
                    this.closeResults();
                }
                break;

            case keyCode.RETURN:
                this.showResults();
                this.arrowing(keyCode.DOWN);
                flag = true;
                break;

            case keyCode.UP:
            case keyCode.DOWN:
                this.arrowing(event.keyCode);
                flag = true;
                break;

            case keyCode.ESC:
                this.inputNode.value = this.inputValue;
                if (this.resultNode) {
                    this.closeResults();
                }
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleResultsKeydown(event) {
        var flag = false;
        switch (event.keyCode) {
            case keyCode.TAB:
                if (this.resultNode) {
                    this.closeResults();
                }
                break;

            case keyCode.RETURN:
                if (this.resultNode) {
                    this.populating();
                    this.closeResults();
                    window.location = event.currentTarget.querySelector('a').href;
                }
                break;

            case keyCode.UP:
            case keyCode.DOWN:
                this.arrowing(event.keyCode);
                flag = true;
                break;

            case keyCode.ESC:
                this.inputNode.value = this.inputValue;
                if (this.resultNode) {
                    this.closeResults();
                }
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleResultsClick(event) {
        event.preventDefault();
        event.stopPropagation();

        // this.inputNode.value = event.currentTarget.querySelector('span').getAttribute('data-item-value');
        this.closeResults();
        window.location = event.currentTarget.querySelector('a').href;
        // this.inputNode.focus();
    }

    handleResultsHover(event) {
        clearSelected();
    }

    handleBackgroundMouseDown(event) {
        if (!this.inputNode.contains(event.target) && !this.resultNode.contains(event.target)) {
            this.isMouseDownOnBackground = true;
            this.closeResults();
            return;
        }
    }

    handleFocus(event) {
        this.openMobileSearchPopup();

        this.clearInputNode.classList.remove('hidden');
        this.clearInputNode.removeAttribute('aria-hidden');;
    }
    
    handleBlur(event) {
        if (this.inputNode.value.length === 0) {
            this.clearInputNode.classList.add('hidden');
            this.clearInputNode.setAttribute('aria-hidden', 'true');
        }
    }

    handleCloseClick(event) {
        event.stopPropagation();
        event.preventDefault();

        this.closeMobileSearchPopup();
    }

    openMobileSearchPopup() {
        if (!bodyElement.classList.contains('searchopen')) {
            bodyElement.classList.add('searchopen');
        }

        if (this.isMenuMobileStyle) {
            this.domNode.style.position = 'fixed';
            this.domNode.style.display = 'block';
            this.domNode.style.zIndex = 100;
            this.navContainerEl.style.zIndex = 12;
        }
    }

    closeMobileSearchPopup() {
        if (bodyElement.classList.contains('searchopen')) {
            bodyElement.classList.remove('searchopen');
        }
        this.closeResults();

        if (mq.matches && this.isMenuMobileStyle) {
            this.domNode.style.position = 'relative';
            this.domNode.style.display = 'block';
            this.domNode.style.zIndex = 0;
            this.navContainerEl.style.zIndex = 1;
        }
    }

    handleBackgroundMouseUp() {
        this.isMouseDownOnBackground = false;
    }

    handleSubmitClick(event) {
        event.stopPropagation();
        event.preventDefault();

        if (!bodyElement.classList.contains('searchopen')) {
            bodyElement.classList.add('searchopen');
        }
        this.arrowing(event.keyCode);
    }

    handleSubmitKeydown(event) {
        var flag = false;

        switch (event.keyCode) {
            case keyCode.TAB:
                if (this.resultNode) {
                    this.closeResults();
                }
                break;

            case keyCode.RETURN:
                this.showResults();
                break;

            case keyCode.UP:
            case keyCode.DOWN:
                this.arrowing(event.keyCode);
                flag = true;
                break;

            case keyCode.ESC:
                this.inputNode.value = this.inputValue;
                if (this.resultNode) {
                    this.closeResults();
                }
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleClearClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.inputNode.value) {
            this.inputNode.value = '';
            this.clearInputNode.classList.add('hidden');
            this.clearInputNode.setAttribute('aria-hidden', 'true');
        }
    }
}

export default AutoComplete;